import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { SITE } from "@/data/site";

const HERO_LINES = ["Transformation,", "not transaction —", "since 2012."];

const lineParent = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.14, delayChildren: 0.35 } },
};
const lineChild = {
  hidden: { y: "110%" },
  visible: { y: "0%", transition: { duration: 1.05, ease: [0.22, 0.62, 0.24, 1] } },
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const rot = useTransform(scrollYProgress, [0, 1], [0, 30]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative min-h-[110vh] hero-vignette overflow-hidden pt-32" data-testid="hero">
      {/* parallax butterfly emblem */}
      <motion.div
        style={{ y, rotate: rot }}
        className="absolute right-[-6%] top-[10%] w-[70vw] max-w-[900px] pointer-events-none"
        aria-hidden
      >
        <ButterflyEmblem />
      </motion.div>

      {/* glow blobs */}
      <div className="butterfly-glow left-[-10%] top-[10%] w-[420px] h-[420px]" style={{ background: "#6A0DAD" }} />
      <div className="butterfly-glow right-[-10%] bottom-[10%] w-[500px] h-[500px]" style={{ background: "#1E1B4B" }} />

      <div className="container-mit relative z-10">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.1 }}
          className="text-[11px] tracking-[0.4em] text-[#A4801C] mb-8"
          data-testid="hero-eyebrow"
        >
          — YOUR PREFERRED TRANSFORMATIONAL PARTNERS
        </motion.p>

        <motion.h1
          initial="hidden"
          animate="visible"
          variants={lineParent}
          className="font-display font-light leading-[0.9] tracking-tight text-[#0A0A14] text-[clamp(48px,10vw,168px)]"
          data-testid="hero-title"
        >
          {HERO_LINES.map((line, i) => (
            <span key={i} className="line-mask">
              <motion.span variants={lineChild} className="line-inner">
                {i === 1 ? (
                  <>
                    not <em className="italic text-gold-grad not-italic font-normal">transaction</em> —
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.5 }}
          className="mt-14 flex flex-col md:flex-row md:items-end md:justify-between gap-10"
        >
          <p className="max-w-md text-[#0A0A14]/70 text-base leading-relaxed">
            A PAN-India institution helping <span className="text-[#0A0A14]">individuals, teams and businesses</span> bridge the gap between AS-IS and TO-BE — with breakthrough innovations, not templates.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <a href="#services" className="btn-editorial" data-testid="hero-cta-services">Explore Services <span className="arrow">→</span></a>
            <a href="#inquiry" className="btn-solid-gold" data-testid="hero-cta-inquiry">Start a Project</a>
          </div>
        </motion.div>
      </div>

      <motion.div style={{ opacity }} className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.4em] text-[#0A0A14]/50">
        SCROLL
        <div className="w-px h-10 mx-auto mt-2 bg-white/30" />
      </motion.div>

      <div className="absolute bottom-0 left-0 right-0 text-center opacity-[0.03] pointer-events-none select-none">
        <div className="font-display text-[22vw] leading-none">{SITE.short}</div>
      </div>
    </section>
  );
}

function ButterflyEmblem() {
  return (
    <svg viewBox="0 0 800 800" width="100%" aria-hidden>
      <defs>
        <radialGradient id="wingA" cx="50%" cy="50%" r="60%">
          <stop offset="0%" stopColor="#FDE047" stopOpacity="0.9" />
          <stop offset="45%" stopColor="#D4AF37" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#1E1B4B" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="wingB" cx="50%" cy="50%" r="60%">
          <stop offset="0%" stopColor="#6A0DAD" stopOpacity="0.9" />
          <stop offset="60%" stopColor="#1E1B4B" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#05050A" stopOpacity="0" />
        </radialGradient>
      </defs>
      <g transform="translate(400,400)">
        <path d="M0 0 C-220 -180, -360 -60, -300 160 C-180 120, -80 60, 0 0 Z" fill="url(#wingB)" />
        <path d="M0 0 C220 -180, 360 -60, 300 160 C180 120, 80 60, 0 0 Z" fill="url(#wingA)" />
        <path d="M0 0 C-140 200, -60 260, 0 180 Z" fill="url(#wingB)" opacity="0.7" />
        <path d="M0 0 C140 200, 60 260, 0 180 Z" fill="url(#wingA)" opacity="0.7" />
        <circle r="4" fill="#FDE047" />
      </g>
    </svg>
  );
}
